import { Loader2, Mail, X } from "lucide-react";
import { useState } from "react";

export default function AdminResolutionModal({
  title,
  fields,
  noteLabel,
  notePlaceholder,
  hasEmail = true,
  sending,
  onClose,
  onConfirm,
}) {
  const [resolutionNote, setResolutionNote] = useState("");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-3 backdrop-blur-sm sm:p-6">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="resolution-modal-title"
        className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-lg bg-white shadow-2xl"
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-gray-50 px-4 py-4 sm:px-6">
          <div>
            <h2
              id="resolution-modal-title"
              className="text-xl font-bold text-gray-900 sm:text-2xl"
            >
              {title}
            </h2>
            <p className="mt-1 text-sm text-gray-600">
              Review the request before confirming resolution.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={sending}
            aria-label="Close resolution modal"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-700 transition hover:bg-red-500 hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
          >
            <X size={20} />
          </button>
        </div>

        <div className="px-4 py-6 sm:px-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {fields.map((field) => (
              <div
                key={field.label}
                className={field.fullWidth ? "sm:col-span-2" : ""}
              >
                <p className="mb-1 text-sm font-medium text-gray-600">
                  {field.label}
                </p>
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-gray-900 whitespace-pre-wrap wrap-break-word">
                  {field.value || "-"}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-5">
            <label
              htmlFor="resolution-note"
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              {noteLabel} <span className="text-gray-400">(optional)</span>
            </label>
            <textarea
              id="resolution-note"
              value={resolutionNote}
              onChange={(event) => setResolutionNote(event.target.value)}
              maxLength={2000}
              rows={5}
              disabled={sending}
              placeholder={notePlaceholder}
              className="w-full resize-y rounded-lg border border-gray-300 px-4 py-3 text-gray-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:bg-gray-100"
            />
            <p className="mt-1 text-right text-xs text-gray-500">
              {resolutionNote.length}/2000
            </p>
          </div>

          {!hasEmail && (
            <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
              No email address is available. This request will be resolved
              without sending an email.
            </div>
          )}

          <div className="mt-6 flex flex-col-reverse gap-3 border-t border-gray-200 pt-5 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={sending}
              className="rounded-lg border border-gray-300 px-5 py-3 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => onConfirm(resolutionNote)}
              disabled={sending}
              className="flex items-center justify-center gap-2 rounded-lg bg-green-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-green-500 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {sending ? (
                <Loader2 className="animate-spin" size={18} />
              ) : (
                <Mail size={18} />
              )}
              {sending
                ? hasEmail
                  ? "Sending..."
                  : "Resolving..."
                : hasEmail
                  ? "Confirm Resolution & Send Email"
                  : "Confirm Resolution"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}